import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Plus, RefreshCw, Trash2, Play, Calendar, RepeatIcon, AlertCircle, CheckCircle } from 'lucide-react';
import api from '../utils/api';
import { formatCurrency } from '../utils/format';
import { useTenant } from '../contexts/TenantContext';
import StatusBadge from '../components/StatusBadge';
import calendarGastifyIcon from '../assets/calendar-gastify.png';

const TIPO_COLORS = { RECEITA: '#339933', DESPESA: '#d72d21', CUSTO: '#c99a1a' };

const FREQ_LABELS = {
  mensal: 'Mensal',
  semanal: 'Semanal',
  quinzenal: 'Quinzenal',
  anual: 'Anual',
};

const FREQ_ICONS = {
  mensal: '📅',
  semanal: '📆',
  quinzenal: '🗓️',
  anual: '🎯',
};

// Mensagem legível para erros do servidor (422 devolve uma lista de {msg})
const erroTexto = (d, padrao) => {
  if (typeof d === 'string') return d;
  if (Array.isArray(d) && d.length) return d.map(i => i.msg || String(i)).join(' — ');
  return padrao;
};

const initialForm = {
  descricao: '',
  tipo: 'DESPESA',
  valor: '',
  categoriaId: '',
  frequencia: 'mensal',
  diaVencimento: '1',
  ativo: true,
};

function RecorrenciaModal({ open, onClose, onSaved, editItem }) {
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [categorias, setCategorias] = useState([]);

  // Categorias do tipo escolhido; mantém a selecionada se ela ainda existir, senão escolhe a primeira
  useEffect(() => {
    if (!open) return;
    api.get(`/categorias?tipo=${form.tipo}`).then(res => {
      const list = res.data || [];
      setCategorias(list);
      setForm(f => (list.some(c => c.id === f.categoriaId) ? f : { ...f, categoriaId: list[0]?.id || '' }));
    }).catch(() => setCategorias([]));
  }, [open, form.tipo]);

  useEffect(() => {
    if (open) {
      setForm(editItem ? {
        descricao: editItem.descricao || '',
        tipo: editItem.tipo || 'DESPESA',
        valor: editItem.valor?.toString() || '',
        categoriaId: editItem.categoriaId || '',
        frequencia: editItem.frequencia || 'mensal',
        diaVencimento: editItem.diaVencimento?.toString() || '1',
        ativo: editItem.ativo !== false,
      } : initialForm);
      setError('');
    }
  }, [open, editItem]);

  if (!open) return null;

  const handleSave = async () => {
    if (!form.descricao.trim()) { setError('Descrição é obrigatória.'); return; }
    const val = parseFloat(form.valor.replace(',', '.'));
    if (!val || val <= 0) { setError('Valor inválido.'); return; }
    if (!form.categoriaId) { setError('Selecione a categoria.'); return; }

    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        valor: val,
        diaVencimento: parseInt(form.diaVencimento) || 1,
        // preserva o que o formulário não edita (senão a edição apagaria a carteira etc.)
        carteiraId: editItem?.carteiraId || null,
        formaPagamento: editItem?.formaPagamento || 'PIX',
        observacoes: (editItem?.observacoes || '').replace(/\s*\[Recorrente\]\s*$/, ''),
      };
      if (editItem) {
        await api.put(`/recorrencias/${editItem.id}`, payload);
      } else {
        await api.post('/recorrencias', payload);
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(erroTexto(err.response?.data?.detail, 'Erro ao salvar.'));
    } finally {
      setSaving(false);
    }
  };

  return createPortal((
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 nexus-modal-overlay">
      <div className="nexus-modal w-full max-w-md">
        <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src={calendarGastifyIcon} alt="" className="w-6 h-6 object-contain" />
            <h2 className="font-bold text-slate-800 dark:text-slate-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {editItem ? 'Editar Recorrência' : 'Nova Recorrência'}
            </h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl font-bold">×</button>
        </div>
        <div className="p-6 space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
              <AlertCircle size={14} /> {error}
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Descrição *</label>
            <input
              className="fin-input w-full"
              placeholder="Ex: Aluguel mensal"
              value={form.descricao}
              onChange={e => setForm(f => ({ ...f, descricao: e.target.value }))}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Tipo *</label>
              <select className="fin-input w-full" value={form.tipo} onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))}>
                <option value="DESPESA">Despesa</option>
                <option value="RECEITA">Receita</option>
                <option value="CUSTO">Custo</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Valor *</label>
              <input
                className="fin-input w-full"
                placeholder="0,00"
                value={form.valor}
                onChange={e => setForm(f => ({ ...f, valor: e.target.value }))}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Frequência</label>
              <select className="fin-input w-full" value={form.frequencia} onChange={e => setForm(f => ({ ...f, frequencia: e.target.value }))}>
                <option value="mensal">Mensal</option>
                <option value="semanal">Semanal</option>
                <option value="quinzenal">Quinzenal</option>
                <option value="anual">Anual</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                {form.frequencia === 'semanal' ? 'Dia da semana (1-7)' : form.frequencia === 'anual' ? 'Mês (1-12)' : 'Dia do venc.'}
              </label>
              <input
                className="fin-input w-full"
                type="number"
                min="1"
                max={form.frequencia === 'semanal' ? 7 : form.frequencia === 'anual' ? 12 : 31}
                value={form.diaVencimento}
                onChange={e => setForm(f => ({ ...f, diaVencimento: e.target.value }))}
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Categoria</label>
            <select
              className="fin-input w-full"
              value={form.categoriaId}
              onChange={e => setForm(f => ({ ...f, categoriaId: e.target.value }))}
              data-testid="rec-categoria-select"
            >
              {categorias.length === 0 && <option value="">Carregando categorias...</option>}
              {categorias.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
          </div>

          <label className="flex items-center gap-3 cursor-pointer">
            <div
              onClick={() => setForm(f => ({ ...f, ativo: !f.ativo }))}
              className={`w-11 h-6 rounded-full transition-colors relative cursor-pointer ${form.ativo ? 'bg-emerald-500' : 'bg-slate-300'}`}
            >
              <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.ativo ? 'translate-x-6' : 'translate-x-1'}`} />
            </div>
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Recorrência ativa</span>
          </label>
        </div>
        <div className="px-6 py-4 border-t border-slate-100 dark:border-slate-700 flex justify-end gap-3">
          <button onClick={onClose} className="fin-btn-secondary">Cancelar</button>
          <button onClick={handleSave} disabled={saving} className="fin-btn-primary">
            {saving ? 'Salvando...' : editItem ? 'Salvar' : 'Criar'}
          </button>
        </div>
      </div>
    </div>
  ), document.body);
}

export default function Recorrencias() {
  const { tenant } = useTenant();
  const accent = tenant?.corPrimaria || '#0147a0';

  const [recorrencias, setRecorrencias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/recorrencias');
      setRecorrencias(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleGerar = async () => {
    setGenerating(true);
    setGenerated(null);
    try {
      const res = await api.post('/recorrencias/gerar');
      setGenerated(res.data.gerados || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remover esta recorrência?')) return;
    setDeletingId(id);
    try {
      await api.delete(`/recorrencias/${id}`);
      setRecorrencias(prev => prev.filter(r => r.id !== id));
    } catch (err) {
      console.error(err);
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggle = async (r) => {
    try {
      const res = await api.put(`/recorrencias/${r.id}`, { ativo: !r.ativo });
      setRecorrencias(prev => prev.map(x => x.id === r.id ? { ...x, ativo: !r.ativo } : x));
    } catch (err) {
      console.error(err);
    }
  };

  const ativas = recorrencias.filter(r => r.ativo);
  const inativas = recorrencias.filter(r => !r.ativo);

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-800 dark:text-slate-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Lançamentos Recorrentes
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            Automatize cobranças e despesas que se repetem periodicamente.
          </p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button
            onClick={handleGerar}
            disabled={generating}
            className="fin-btn-secondary text-xs flex items-center gap-1.5"
          >
            <Play size={13} className={generating ? 'animate-pulse' : ''} />
            {generating ? 'Gerando...' : 'Gerar agora'}
          </button>
          <button
            onClick={() => { setEditItem(null); setModalOpen(true); }}
            className="fin-btn-primary text-xs flex items-center gap-1.5"
            style={{ background: accent }}
          >
            <Plus size={14} /> Nova
          </button>
        </div>
      </div>

      {/* Generated alert */}
      {generated !== null && (
        <div className={`flex items-center gap-3 p-4 rounded-2xl border ${generated > 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'}`}>
          <CheckCircle size={18} className={generated > 0 ? 'text-emerald-500' : 'text-slate-400'} />
          <p className="text-sm font-medium">
            {generated > 0
              ? `${generated} lançamento${generated !== 1 ? 's' : ''} gerado${generated !== 1 ? 's' : ''} com sucesso!`
              : 'Nenhum lançamento para gerar no momento.'}
          </p>
          <button onClick={() => setGenerated(null)} className="ml-auto text-slate-400 hover:text-slate-600">×</button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {Object.entries(FREQ_LABELS).map(([freq, label]) => {
          const count = recorrencias.filter(r => r.frequencia === freq && r.ativo).length;
          return (
            <div key={freq} className="fin-card p-4">
              <div className="w-8 h-8 rounded-xl bg-teal-50/80 dark:bg-teal-950/40 p-1 flex items-center justify-center">
                <img src={calendarGastifyIcon} alt="" className="w-full h-full object-contain" />
              </div>
              <p className="text-lg font-bold text-slate-800 dark:text-slate-100 mt-2">{count}</p>
              <p className="text-xs text-slate-400 font-medium">{label}</p>
            </div>
          );
        })}
      </div>

      {/* List */}
      <div className="fin-card">
        <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between">
          <p className="font-semibold text-slate-700 dark:text-slate-200 text-sm">
            {recorrencias.length} recorrência{recorrencias.length !== 1 ? 's' : ''} cadastrada{recorrencias.length !== 1 ? 's' : ''}
          </p>
          <button onClick={load} className="text-slate-400 hover:text-slate-600 transition-colors">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>

        {loading ? (
          <div className="p-10 text-center text-slate-400 text-sm">Carregando...</div>
        ) : recorrencias.length === 0 ? (
          <div className="p-12 text-center">
            <img src={calendarGastifyIcon} alt="" className="w-16 h-16 mx-auto mb-3 object-contain drop-shadow-sm" />
            <p className="font-semibold text-slate-600 dark:text-slate-300">Nenhuma recorrência</p>
            <p className="text-sm text-slate-400 mt-1">Crie sua primeira recorrência para automatizar lançamentos.</p>
          </div>
        ) : (
          <>
          <div className="md:hidden divide-y divide-slate-100 dark:divide-slate-700" data-testid="recorrencias-cards">
            {recorrencias.map(r => (
              <div key={r.id} className={`p-4 space-y-3 ${!r.ativo ? 'opacity-50' : ''}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">{r.descricao}</p>
                    {r.categoria && <p className="text-xs text-slate-400 truncate">{r.categoria}</p>}
                  </div>
                  <p className="text-sm font-bold whitespace-nowrap" style={{ color: TIPO_COLORS[r.tipo] }}>{formatCurrency(r.valor)}</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap text-xs">
                  <StatusBadge type="tipo" value={r.tipo} />
                  <span className="inline-flex items-center gap-1.5 font-semibold px-2.5 py-1 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                    <img src={calendarGastifyIcon} alt="" className="w-3.5 h-3.5 object-contain" />
                    {FREQ_LABELS[r.frequencia]} · dia {r.diaVencimento}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <button
                    onClick={() => handleToggle(r)}
                    aria-label={r.ativo ? 'Desativar recorrência' : 'Ativar recorrência'}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors ${r.ativo ? 'bg-emerald-500' : 'bg-slate-300'}`}
                  >
                    <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${r.ativo ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => { setEditItem(r); setModalOpen(true); }}
                      className="text-xs font-semibold px-3.5 py-2 rounded-xl bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200"
                    >Editar</button>
                    <button
                      onClick={() => handleDelete(r.id)}
                      disabled={deletingId === r.id}
                      aria-label="Excluir recorrência"
                      className="px-3 py-2 rounded-xl bg-red-50 text-red-500 disabled:opacity-50"
                    ><Trash2 size={14} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-700/50 border-b border-slate-100 dark:border-slate-700">
                  {['Descrição', 'Tipo', 'Frequência', 'Dia', 'Valor', 'Status', ''].map(h => (
                    <th key={h} className="text-left px-5 py-3 text-xs font-bold uppercase tracking-widest text-slate-400">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50 dark:divide-slate-700">
                {recorrencias.map(r => (
                  <tr key={r.id} className={`hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors ${!r.ativo ? 'opacity-50' : ''}`}>
                    <td className="px-5 py-3">
                      <p className="font-medium text-slate-800 dark:text-slate-100">{r.descricao}</p>
                      {r.categoria && <p className="text-xs text-slate-400">{r.categoria}</p>}
                    </td>
                    <td className="px-5 py-3"><StatusBadge type="tipo" value={r.tipo} /></td>
                    <td className="px-5 py-3">
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                        <img src={calendarGastifyIcon} alt="" className="w-3.5 h-3.5 object-contain" />
                        <span>{FREQ_LABELS[r.frequencia]}</span>
                      </span>
                    </td>
                    <td className="px-5 py-3 text-slate-600 dark:text-slate-300 font-medium">
                      {r.frequencia === 'semanal' ? `Dia ${r.diaVencimento}` : `Dia ${r.diaVencimento}`}
                    </td>
                    <td className="px-5 py-3 font-bold whitespace-nowrap" style={{ color: TIPO_COLORS[r.tipo] }}>
                      {formatCurrency(r.valor)}
                    </td>
                    <td className="px-5 py-3">
                      <button
                        onClick={() => handleToggle(r)}
                        className={`relative inline-flex h-5 w-9 rounded-full transition-colors ${r.ativo ? 'bg-emerald-500' : 'bg-slate-300'}`}
                      >
                        <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${r.ativo ? 'translate-x-4' : 'translate-x-0.5'}`} />
                      </button>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => { setEditItem(r); setModalOpen(true); }}
                          className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-slate-50 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 transition-colors"
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => handleDelete(r.id)}
                          disabled={deletingId === r.id}
                          className="text-xs font-semibold px-2 py-1 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition-colors disabled:opacity-50"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>

      <RecorrenciaModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={load}
        editItem={editItem}
      />
    </div>
  );
}
